// LivestreamPaymentTimerHandler.jsx
'use strict';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import setIsPrivateStreamUnlocked from '../action/setIsPrivateStreamUnlocked.js';
import submitStreamPayment from '../action/submitStreamPayment.js';
import setShouldSubmitStreamPaymentOnSdPreset from '../action/setShouldSubmitStreamPaymentOnSdPreset.js';

import useInterval from '../hook/useInterval.js';

import getLiveStreamData from '../selector/getLiveStreamData.js';
import getOperationData from '../selector/getOperationData.js';
import getStreamTokenDecodedData from '../selector/getStreamTokenDecodedData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';

import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';

import {
  PRESET_SD,
  PRESET_PREVIEW,
  PRECHARGE_SECONDS,
  PAYMENT_CHECK_INTERVAL_SECONDS,
  livestreamPaymentVia as livestreamPaymentViaConstant,
  ExclusivePrivateStatus,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';
import { livestream as livestreamDebug } from '../resource/debug.js';

const log = livestreamDebug.extend('log:LivestreamPaymentTimerHandler');

const LivestreamPaymentTimerHandler = ({ streamId = '' }) => {
  const dispatch = useDispatch();

  const streamStatus = useSelector(state =>
    getLiveStreamData(state, [streamId], 'status')
  );
  const decodedToken = useSelector(state =>
    getStreamTokenDecodedData(state, streamId)
  );
  const isAutoPaymentEnabled = useSelector(state =>
    getOperationData(
      state,
      ['liveStream', 'autoPayment', streamId],
      'isEnabled'
    )
  );
  const privateShowStatus = useSelector(state =>
    getPrivateShowStatus(state, streamId)
  );
  const exclusivePrivateStatus = useSelector(state =>
    getLiveStreamData(state, [streamId, 'exclusivePrivate'], 'status')
  );
  const sessionId = useSelector(state =>
    getLiveStreamData(state, [streamId], 'sessionId')
  );
  const shouldSubmitStreamPaymentOnSdPreset = useSelector(state =>
    getOperationData(
      state,
      ['liveStream', 'paid', streamId, sessionId],
      'shouldSubmitStreamPaymentOnSdPreset'
    )
  );
  const timestampOffsetSeconds = useSelector(state =>
    getTimestampOffset(state, Accuracy.SECOND)
  );
  const sdNbf = useSelector(state =>
    getLiveStreamData(state, [streamId, 'presets', PRESET_SD], 'nbf')
  );
  const counterExp = useSelector(state =>
    getLiveStreamData(state, [streamId, 'presets', PRESET_SD, 'counter'], 'exp')
  );

  const isPrivateShowPreparing =
    privateShowStatusConstant.PREPARING === privateShowStatus;
  const isPrivateShowPerforming =
    privateShowStatusConstant.PERFORMING === privateShowStatus;
  const isExclusivePrivatePreparing =
    ExclusivePrivateStatus.PREPARING === exclusivePrivateStatus;
  const isExclusivePrivatePerforming =
    ExclusivePrivateStatus.PERFORMING === exclusivePrivateStatus;
  const sdSwitchTimestamp = sdNbf || counterExp;

  useInterval({
    canExecute:
      isAutoPaymentEnabled &&
      !isPrivateShowPerforming &&
      !isPrivateShowPreparing,
    intervalSeconds: PAYMENT_CHECK_INTERVAL_SECONDS,
    execute: () => {
      const now = getCurrentUnixTimestamp({
        offsetSeconds: timestampOffsetSeconds,
      });

      const hasValidStreamToken =
        !!decodedToken && now < decodedToken.exp && now >= decodedToken.nbf;

      const logData = {
        streamId,
        sessionId,
        privateShowStatus,
        exclusivePrivateStatus,
        shouldSubmitStreamPaymentOnSdPreset,
        streamStatus,
        now,
        tokenNbf: decodedToken?.nbf,
        tokenExp: decodedToken?.exp,
        sdSwitchTimestamp,
        PRECHARGE_SECONDS,
      };

      log('execute submit payment check function', logData);

      // Condition 1: periodic auto-renew payment
      if (
        streamStatus === PRESET_SD &&
        hasValidStreamToken &&
        now >= decodedToken.exp - PRECHARGE_SECONDS
      ) {
        log('execute periodic auto-renew payment', logData);

        dispatch(
          submitStreamPayment({
            streamId,
            sessionId,
            via: livestreamPaymentViaConstant.AUTO,
          })
        );
        return;
      }

      // Condition 2: unlock private stream, already on SD
      if (streamStatus === PRESET_SD && shouldSubmitStreamPaymentOnSdPreset) {
        log('execute unlock private stream on SD', logData);

        dispatch(
          setShouldSubmitStreamPaymentOnSdPreset({
            streamId,
            sessionId,
            shouldSubmitStreamPaymentOnSdPreset: false,
          })
        );
        dispatch(
          submitStreamPayment({
            streamId,
            sessionId,
            via: livestreamPaymentViaConstant.CLICK,
          })
        );
        return;
      }

      // Condition 3: unlock private stream, wait for SD switch
      // * We pre-charge exclusive private on inviting, so no need to payment on SD switching
      if (
        streamStatus === PRESET_PREVIEW &&
        shouldSubmitStreamPaymentOnSdPreset &&
        sdSwitchTimestamp &&
        now >= sdSwitchTimestamp - PRECHARGE_SECONDS &&
        !isExclusivePrivatePreparing &&
        !isExclusivePrivatePerforming
      ) {
        log('execute unlock private stream, approaching SD switch', logData);

        dispatch(
          setShouldSubmitStreamPaymentOnSdPreset({
            streamId,
            sessionId,
            shouldSubmitStreamPaymentOnSdPreset: false,
          })
        );
        dispatch(
          submitStreamPayment({
            streamId,
            sessionId,
            via: livestreamPaymentViaConstant.CLICK,
          })
        );
        return;
      }
    },
  });

  // Cleanup on unmount / streamId / sessionId change
  useEffect(() => {
    return () => {
      dispatch(
        setShouldSubmitStreamPaymentOnSdPreset({
          streamId,
          sessionId,
          shouldSubmitStreamPaymentOnSdPreset: false,
        })
      );
      dispatch(
        setIsPrivateStreamUnlocked({
          streamId,
          isPrivateStreamUnlocked: false,
        })
      );
    };
  }, [dispatch, streamId, sessionId]);

  return null;
};

LivestreamPaymentTimerHandler.propTypes = {
  streamId: PropTypes.string,
};

export { LivestreamPaymentTimerHandler };
export default LivestreamPaymentTimerHandler;
